import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, User, Menu, X, LogOut, LayoutDashboard, Heart } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useWishlist } from '../../contexts/WishlistContext';
import { useSiteSettings } from '../../hooks/useSiteSettings';
import { SearchWithAutocomplete } from '../common/SearchWithAutocomplete';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export const Navbar = () => {
  const { t } = useTranslation();
  const { user, logout, isAdmin } = useAuth();
  const { getCartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { getSetting } = useSiteSettings();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 mr-8">
            <div className="w-10 h-10 bg-gradient-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">
                {getSetting('logo_icon_text', 'NC')}
              </span>
            </div>
            <span className="font-serif text-2xl font-bold text-foreground hidden sm:block">
              {getSetting('site_name', 'NaturalCare')}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-sm font-medium hover:text-primary transition-smooth">
              {t('nav.home')}
            </Link>
            <Link to="/shop" className="text-sm font-medium hover:text-primary transition-smooth">
              {t('nav.shop')}
            </Link>
            <Link to="/categories" className="text-sm font-medium hover:text-primary transition-smooth">
              {t('nav.categories')}
            </Link>
            <Link to="/about" className="text-sm font-medium hover:text-primary transition-smooth">
              {t('nav.about')}
            </Link>
            <Link to="/contact" className="text-sm font-medium hover:text-primary transition-smooth">
              {t('nav.contact')}
            </Link>
          </div>

          {/* Search Bar with Autocomplete */}
          <SearchWithAutocomplete className="hidden lg:flex flex-1 max-w-md mx-8" />

          {/* Right Actions */}
          <div className="flex items-center space-x-4">
            {/* Language Switcher */}
            <LanguageSwitcher />

            {isAdmin && (
              <Link to="/admin">
                <Button variant="ghost" size="icon" className="hidden md:flex">
                  <LayoutDashboard className="w-5 h-5" />
                </Button>
              </Link>
            )}

            {user ? (
              <>
                <Link to="/profile" className="hidden md:block">
                  <Button variant="ghost" size="icon">
                    <User className="w-5 h-5" />
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  className="hidden md:flex"
                >
                  <LogOut className="w-5 h-5" />
                </Button>
              </>
            ) : null}

            <Link to="/cart" className="relative">
              <Button variant="ghost" size="icon">
                <ShoppingCart className="w-5 h-5" />
                {getCartCount() > 0 && (
                  <Badge
                    variant="default"
                    className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 text-xs"
                  >
                    {getCartCount()}
                  </Badge>
                )}
              </Button>
            </Link>

            {/* Wishlist Icon */}
            {user && (
              <Link to="/wishlist" className="relative hidden md:block">
                <Button variant="ghost" size="icon">
                  <Heart className="w-5 h-5" />
                  {wishlistCount > 0 && (
                    <Badge
                      variant="default"
                      className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 text-xs"
                    >
                      {wishlistCount}
                    </Badge>
                  )}
                </Button>
              </Link>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t">
            <div className="flex flex-col space-y-4">
              <SearchWithAutocomplete className="lg:hidden" />
              <Link
                to="/"
                className="text-sm font-medium hover:text-primary transition-smooth"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.home')}
              </Link>
              <Link
                to="/shop"
                className="text-sm font-medium hover:text-primary transition-smooth"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.shop')}
              </Link>
              <Link
                to="/categories"
                className="text-sm font-medium hover:text-primary transition-smooth"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.categories')}
              </Link>
              <Link
                to="/about"
                className="text-sm font-medium hover:text-primary transition-smooth"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.about')}
              </Link>
              <Link
                to="/contact"
                className="text-sm font-medium hover:text-primary transition-smooth"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.contact')}
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="text-sm font-medium hover:text-primary transition-smooth"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.adminDashboard')}
                </Link>
              )}
              {user && (
                <>
                  <Link
                    to="/profile"
                    className="text-sm font-medium hover:text-primary transition-smooth"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.myProfile')}
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="text-sm font-medium hover:text-primary transition-smooth text-left"
                  >
                    {t('nav.signOut')}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};